// <s-delivery-estimate> shows an Istanbul-time shipping cutoff countdown and an
// estimated delivery date per carrier, remembering the buyer's carrier and province.
const TZ = 'Europe/Istanbul';
const STORE_CARRIER = 'delivery_estimate_carrier';
const STORE_PROVINCE = 'delivery_estimate_province';

function istanbulParts(date = new Date()) {
  const parts = {};
  new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })
    .formatToParts(date)
    .forEach((p) => {
      parts[p.type] = p.value;
    });
  return parts;
}

function isBusinessDay(date) {
  const day = date.getUTCDay();
  return day !== 0 && day !== 6;
}

function addBusinessDays(date, days) {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setUTCDate(result.getUTCDate() + 1);
    if (isBusinessDay(result)) added += 1;
  }
  return result;
}

class DeliveryEstimate extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.cutoff = Number(this.dataset.cutoffHour || 14);
    this.locale = document.documentElement.lang || 'tr';
    this.headline = this.querySelector('[data-delivery-headline]');
    this.countdown = this.querySelector('[data-delivery-countdown]');
    this.countdownWrap = this.querySelector('[data-delivery-countdown-wrap]');
    this.province = this.querySelector('[data-delivery-province]');
    this.provinceLabel = this.querySelector('[data-delivery-province-label]');
    this.carriers = [...this.querySelectorAll('[data-delivery-carrier]')];

    try {
      const savedCarrier = localStorage.getItem(STORE_CARRIER);
      const savedProvince = localStorage.getItem(STORE_PROVINCE);
      const match = this.carriers.find((c) => c.value === savedCarrier);
      if (match) match.checked = true;
      if (savedProvince && this.province) this.province.value = savedProvince;
    } catch (error) {
      // Storage can be unavailable in private browsing; defaults still render.
    }

    this.carriers.forEach((input) =>
      input.addEventListener('change', () => this.update(true), { signal }),
    );
    this.province?.addEventListener('change', () => this.update(true), { signal });

    this.update(false);
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearInterval(this.timer);
  }

  selectedCarrier() {
    return this.carriers.find((c) => c.checked) || this.carriers[0];
  }

  shipDate() {
    const p = istanbulParts();
    const today = new Date(Date.UTC(+p.year, +p.month - 1, +p.day));
    const beforeCutoff = +p.hour < this.cutoff;
    if (isBusinessDay(today) && beforeCutoff) return { date: today, today: true };
    let next = new Date(today);
    do {
      next.setUTCDate(next.getUTCDate() + 1);
    } while (!isBusinessDay(next));
    return { date: next, today: false };
  }

  format(date) {
    return new Intl.DateTimeFormat(this.locale, {
      timeZone: 'UTC',
      day: 'numeric',
      month: 'long',
      weekday: 'long',
    }).format(date);
  }

  update(persist) {
    const carrier = this.selectedCarrier();
    if (!carrier) return;
    const min = Number(carrier.dataset.min || 1);
    const max = Number(carrier.dataset.max || min);
    const { date } = this.shipDate();
    const minDate = addBusinessDays(date, min);
    const maxDate = addBusinessDays(date, max);
    const range = min === max ? this.format(maxDate) : `${this.format(minDate)} – ${this.format(maxDate)}`;

    if (this.headline) {
      const template = this.dataset.headlineTemplate || '[date]';
      const [before, after] = template.split('[date]');
      const strong = document.createElement('strong');
      strong.className = 'text-interactive';
      strong.textContent = range;
      this.headline.replaceChildren(document.createTextNode(before || ''), strong, document.createTextNode(after || ''));
    }

    if (this.provinceLabel && this.province) {
      this.provinceLabel.textContent = this.province.value;
      this.provinceLabel.parentElement.hidden = !this.province.value;
    }

    if (persist) {
      try {
        localStorage.setItem(STORE_CARRIER, carrier.value);
        if (this.province) localStorage.setItem(STORE_PROVINCE, this.province.value);
      } catch (error) {
        // Ignore storage failures.
      }
    }
    this.tick();
  }

  tick() {
    if (!this.countdown || !this.countdownWrap) return;
    const p = istanbulParts();
    const today = new Date(Date.UTC(+p.year, +p.month - 1, +p.day));
    const secondsLeft = this.cutoff * 3600 - (+p.hour * 3600 + +p.minute * 60 + +p.second);
    const active = isBusinessDay(today) && secondsLeft > 0;
    this.countdownWrap.hidden = !active;
    if (!active) return;
    const h = Math.floor(secondsLeft / 3600);
    const m = Math.floor((secondsLeft % 3600) / 60);
    const s = secondsLeft % 60;
    const pad = (n) => String(n).padStart(2, '0');
    this.countdown.textContent = `${pad(h)}:${pad(m)}:${pad(s)}`;
  }
}

if (!customElements.get('s-delivery-estimate')) {
  customElements.define('s-delivery-estimate', DeliveryEstimate);
}
